namespace $ {

	export type $shm_hitalama_map_journal_analysis_event_record = {
		signature: string
		widget: string
		geometry: $shm_hitalama_map_journal_snapshots_geometry_event['geometry']
		recalculations: Set< string >
		first: number
		write_time: number | null
		writers: Set< string >
		by_client: Map< string, number >
	}

	export class $shm_hitalama_map_journal_analysis {

		static app_version() {
			return $mol_error_fence(
				()=> $shm_hitalama_app.Root(0).Footer().Version().version(),
				()=> '',
				()=> '',
			)
		}

		static event_signature( event: $shm_hitalama_map_journal_snapshots_geometry_event ) {
			return [
				event.type,
				event.widget,
				event.geometry.body_x,
				event.geometry.body_y,
				event.geometry.right_edge_x,
				event.geometry.bottom_edge_y,
			].join( '|' )
		}

		static data( journal: $shm_hitalama_map_journal_snapshots, run: string ) {
			const clients = journal.clients()
			const app_version = this.app_version()
			const snapshots = clients
				.map( client => journal.snapshot( client ) )
				.filter( ( snapshot ): snapshot is $shm_hitalama_map_journal_snapshots_snapshot => snapshot?.run === run )
				.filter( ( snapshot ): snapshot is $shm_hitalama_map_journal_snapshots_snapshot => Boolean( snapshot ) )
			if( snapshots.length < clients.length ) return null
			const client_infos = new Map( snapshots.map( snapshot => [
				snapshot.client,
				snapshot.client_info ?? journal.client_info( snapshot.client ) ?? { client_id: snapshot.client },
			] ) )
			const client_label = ( client: string )=> {
				const info = client_infos.get( client )
				const short = client.slice( 0, 8 )
				const title = info?.name ? `${ info.name } (${ short })` : short
				const meta = [ info?.device, info?.browser ].filter( Boolean ).join( ' / ' )
				return meta ? `${ title } ${ meta }` : title
			}
			const client_labels = clients.map( client_label )

			const event_count = snapshots.reduce( ( sum, snapshot ) => sum + snapshot.events.length, 0 )
			const signature = JSON.stringify( snapshots.map( snapshot => [
				snapshot.client,
				snapshot.client_info,
				snapshot.written_at,
				... snapshot.events.map( event => [
					event.type,
					event.recalculation,
					event.widget,
					event.time,
					event.geometry.body_x,
					event.geometry.body_y,
					event.geometry.right_edge_x,
					event.geometry.bottom_edge_y,
				].join( ':' ) ),
			] ) )

			const write_events = snapshots.reduce(
				( sum, snapshot ) => sum + snapshot.events.filter( event => event.recalculation === 'write' ).length,
				0,
			)
			const read_events = event_count - write_events
			const written_at = snapshots.map( snapshot => snapshot.written_at ).filter( Boolean )
			const times = snapshots.flatMap( snapshot => snapshot.events.map( event => event.time ) )
			const started = journal.started_at() || ( times.length ? Math.min( ... times ) : Math.min( ... written_at ) )
			const finished = journal.stopped_at() || ( written_at.length ? Math.max( ... written_at ) : Math.max( ... times ) )
			const duration = Number.isFinite( started ) && Number.isFinite( finished )
				? Math.max( 0, finished - started )
				: 0

			const events_by_signature = new Map< string, $shm_hitalama_map_journal_analysis_event_record >()

			for( const snapshot of snapshots ) {
				for( const event of snapshot.events ) {
					const signature = this.event_signature( event )
					const record = events_by_signature.get( signature ) ?? {
						signature,
						widget: event.widget,
						geometry: event.geometry,
						recalculations: new Set< string >,
						first: event.time,
						write_time: null,
						writers: new Set< string >,
						by_client: new Map< string, number >,
					}

					record.first = Math.min( record.first, event.time )
					record.recalculations.add( event.recalculation )
					if( event.recalculation === 'write' ) {
						record.writers.add( snapshot.client )
						record.write_time = record.write_time === null
							? event.time
							: Math.min( record.write_time, event.time )
					}
					const prev = record.by_client.get( snapshot.client )
					if( prev === undefined || event.time < prev ) record.by_client.set( snapshot.client, event.time )
					events_by_signature.set( signature, record )
				}
			}

			const chart_events = [ ... events_by_signature.values() ]
				.filter( event => event.write_time !== null )
				.sort( ( left, right )=> left.write_time! - right.write_time! )
			const chart_rows = chart_events.map( ( event, index )=> [
				[
					`#${ index + 1 }`,
					event.widget.slice( 0, 8 ),
					`${ event.geometry.body_x },${ event.geometry.body_y }`,
					`${ event.geometry.right_edge_x }x${ event.geometry.bottom_edge_y }`,
				].join( ' ' ),
				... clients.map( client => {
					if( event.writers.has( client ) ) return 0
					const time = event.by_client.get( client )
					return time === undefined ? '' : Math.max( 0, Math.round( time - event.write_time! ) )
				} ),
			] )

			const lag_by_client = new Map< string, number[] >( clients.map( client => [ client, [] ] ) )
			for( const event of chart_events ) {
				for( const client of clients ) {
					if( event.writers.has( client ) ) continue
					const time = event.by_client.get( client )
					if( time === undefined ) continue
					lag_by_client.get( client )?.push( Math.max( 0, Math.round( time - event.write_time! ) ) )
				}
			}

			const details_rows = snapshots.map( snapshot => {
				const info = client_infos.get( snapshot.client )
				const writes = snapshot.events.filter( event => event.recalculation === 'write' ).length
				const lags = lag_by_client.get( snapshot.client ) ?? []
				return [
					snapshot.client,
					info?.name ?? '',
					info?.device ?? '',
					info?.browser ?? '',
					snapshot.events.length,
					writes,
					snapshot.events.length - writes,
					lags.length ? Math.min( ... lags ) : '',
					lags.length ? Math.max( ... lags ) : '',
				]
			})

			const summary_text = [
				'# Benchmark analysis',
				`Run: ${ run }`,
				`Hitalama version: ${ app_version || '-' }`,
				`Clients: ${ snapshots.length }/${ clients.length } (you: {{client_id}})`,
				`Snapshots: ${ snapshots.length }/${ clients.length }`,
				`Geometry events: ${ event_count }`,
				`Writes: ${ write_events }`,
				`Reads: ${ read_events }`,
				`Duration: ${ Math.round( duration ) } ms`,
			].join( '\n' )

			const details_text = [
				'# Benchmark analysis events',
				'| client_id | name | device | browser | events | writes | reads | min lag ms | max lag ms |',
				'| --- | --- | --- | --- | ---: | ---: | ---: | ---: | ---: |',
				... details_rows.map( row => `| ${ row.join( ' | ' ) } |` ),
			].join( '\n' )

			return {
				clients,
				client_labels,
				snapshots,
				app_version,
				signature,
				summary_text,
				details_text,
				chart_rows,
			}
		}

	}

}
